import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  UploadCloud, Cpu, Rocket, Activity, ScrollText, ShieldCheck,
  Boxes, SlidersHorizontal, Server,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Sparkline from '../components/Sparkline';
import '../assets/css/landing.css';

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return reduced;
}

function fmtUptime(totalSec) {
  const d = Math.floor(totalSec / 86400);
  const h = String(Math.floor((totalSec % 86400) / 3600)).padStart(2, '0');
  const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
  const s = String(totalSec % 60).padStart(2, '0');
  return `${d}d ${h}:${m}:${s}`;
}

const LOG_LINES = [
  'OnTimer  equity 10,482.50  open 2',
  'trade  BUY 0.20 EURUSD @ 1.08421',
  'OnTick  spread 0.4  ok',
  'OnTimer  equity 10,498.10  open 2',
  'trade  TP hit  +31.60',
  'OnTimer  equity 10,529.70  open 1',
  'heartbeat  cpu 4%  mem 318MB',
];

/* ── Signature: the live runtime panel ─────────────────────────
   A hosted EA, RUNNING — uptime ticks, equity drifts, logs stream.
   All motion is gated on prefers-reduced-motion. */
function RuntimePanel({ reduced }) {
  const [secs, setSecs] = useState(14 * 86400 + 6 * 3600 + 22 * 60 + 9);
  const [feed, setFeed] = useState(LOG_LINES.slice(0, 4));
  const [equity, setEquity] = useState([5, 5.3, 5.1, 5.6, 5.4, 6.0, 6.2, 6.1, 6.6, 6.9]);
  const idx = useRef(4);

  useEffect(() => {
    if (reduced) return;
    const t = setInterval(() => setSecs(s => s + 1), 1000);
    const l = setInterval(() => {
      setFeed(prev => [...prev.slice(1), LOG_LINES[idx.current % LOG_LINES.length]]);
      idx.current += 1;
    }, 2100);
    const e = setInterval(() => {
      setEquity(prev => {
        const next = prev[prev.length - 1] + (Math.sin(idx.current) * 0.4 + 0.15);
        return [...prev.slice(1), Math.max(4, next)];
      });
    }, 2100);
    return () => { clearInterval(t); clearInterval(l); clearInterval(e); };
  }, [reduced]);

  return (
    <div className="rt">
      <div className="rt-head">
        <span className="rt-file">momentum.mq5</span>
        <span className="rt-status"><span className="rt-dot" /> RUNNING</span>
      </div>
      <div className="rt-meta">
        <span>EURUSD · H1</span>
        <span className="rt-up">uptime {fmtUptime(secs)}</span>
      </div>
      <div className="rt-equity">
        <Sparkline values={equity} height={48} stroke="#2DD4BF" />
      </div>
      <div className="rt-log">
        {feed.map((line, i) => (
          <div className="rt-line" key={`${line}-${i}`}>
            <span className="rt-ts">›</span> {line}
          </div>
        ))}
      </div>
    </div>
  );
}

const STEPS = [
  { Icon: UploadCloud, t: 'Upload your .mq5', d: 'Drop in your Expert Advisor source. We accept MQL5 source only — your compiled IP is built in our sandbox, not handed over.' },
  { Icon: Cpu,         t: 'We compile & sandbox', d: 'Server-side compilation under MetaEditor, plus a source scan that rejects DLL imports before anything runs.' },
  { Icon: Rocket,      t: 'Deploy to MT5', d: 'Pick a symbol, timeframe, and inputs. Your EA is attached to a chart in a dedicated terminal and starts trading.' },
  { Icon: Activity,    t: 'Monitor live', d: 'Watch equity, trades, EA status, and raw logs in real time. Restart or stop any deployment in one click.' },
];

const FEATURES = [
  { Icon: Server,            t: 'A terminal of your own', d: 'Every customer runs in an isolated MT5 container — never a shared session. Your account, your charts, your EAs.' },
  { Icon: Cpu,               t: 'Server-side compilation', d: 'Upload source; we compile it under MetaEditor and surface the exact build log if anything fails.' },
  { Icon: ShieldCheck,       t: 'Source-only security', d: 'Compiled uploads are refused and every source is scanned for DLL imports — customer code can\'t smuggle in native calls.' },
  { Icon: SlidersHorizontal, t: 'Deploy with inputs', d: 'Set each EA\'s parameters at deploy time. Inputs are written straight into the chart profile — no manual setup.' },
  { Icon: ScrollText,        t: 'Live logs & equity', d: 'Stream the EA\'s own journal and watch equity, open trades, and heartbeats update as they happen.' },
  { Icon: Boxes,             t: 'Capped & contained', d: 'Per-customer RAM and CPU limits keep one runaway EA from touching anyone else — a leak OOM-kills itself.' },
];

const PLANS = [
  { name: 'Starter', price: 'Free', note: 'during beta', feats: ['1 running EA', '1 broker account', 'Live monitoring & logs'], cta: 'Start free' },
  { name: 'Pro', price: '$—', note: 'coming soon', feats: ['Up to 5 running EAs', 'Priority compilation', 'Email & Discord alerts'], cta: 'Get started', featured: true },
  { name: 'Scale', price: 'Custom', note: 'talk to us', feats: ['More EAs & accounts', 'Higher resource caps', 'Hands-on onboarding'], cta: 'Contact us' },
];

export default function Landing() {
  const { user } = useAuth();
  const reduced = useReducedMotion();
  const year = useRef(new Date().getFullYear());

  return (
    <div className="lp">
      <header className="lp-nav">
        <div className="lp-shell lp-nav-inner">
          <Link to="/" className="lp-brand"><span className="lp-mark" aria-hidden="true" />EAHost</Link>
          <nav className="lp-nav-links">
            <a href="#how">How it works</a>
            <a href="#features">Features</a>
            <a href="#security">Security</a>
            <a href="#pricing">Pricing</a>
          </nav>
          <div className="lp-nav-cta">
            {user ? (
              <Link to={`/${user.role}`} className="lp-btn lp-btn-primary">Go to dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="lp-btn lp-btn-ghost">Sign in</Link>
                <Link to="/register" className="lp-btn lp-btn-primary">Get started</Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="lp-hero">
        <div className="lp-shell lp-hero-grid">
          <div className="lp-hero-copy">
            <span className="lp-eyebrow">MT5 EA hosting</span>
            <h1 className="lp-h1">Your EAs,<br />running 24/7.</h1>
            <p className="lp-lede">
              Stop babysitting a VPS. Upload your MetaTrader 5 Expert Advisor, connect your
              broker, and we run it around the clock on isolated, managed infrastructure —
              compiled, sandboxed, and monitored.
            </p>
            <div className="lp-hero-actions">
              <Link to="/register" className="lp-btn lp-btn-primary lp-btn-lg">Deploy your first EA</Link>
              <a href="#how" className="lp-btn lp-btn-outline lp-btn-lg">See how it works</a>
            </div>
            <dl className="lp-hero-stats">
              <div><dt>Uptime target</dt><dd className="mono">99.9%</dd></div>
              <div><dt>Isolation</dt><dd className="mono">1:1</dd></div>
              <div><dt>Setup</dt><dd className="mono">~mins</dd></div>
            </dl>
          </div>
          <div className="lp-hero-rt">
            <RuntimePanel reduced={reduced} />
          </div>
        </div>
      </section>

      {/* How it works — a real pipeline → numbered */}
      <section className="lp-section" id="how">
        <div className="lp-shell">
          <header className="lp-sec-head">
            <span className="lp-eyebrow">The pipeline</span>
            <h2 className="lp-h2">From source file to live trading, in four steps.</h2>
          </header>
          <ol className="lp-steps">
            {STEPS.map(({ Icon, t, d }, i) => (
              <li key={t}>
                <div className="lp-step-row">
                  <span className="lp-step-no mono">{String(i + 1).padStart(2, '0')}</span>
                  <span className="lp-step-glyph"><Icon size={18} /></span>
                </div>
                <h3>{t}</h3>
                <p>{d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Features */}
      <section className="lp-section lp-section-alt" id="features">
        <div className="lp-shell">
          <header className="lp-sec-head">
            <span className="lp-eyebrow">What you get</span>
            <h2 className="lp-h2">Hosting built for the way EAs actually run.</h2>
          </header>
          <div className="lp-feature-grid">
            {FEATURES.map(({ Icon, t, d }) => (
              <article className="lp-feature" key={t}>
                <span className="lp-feature-glyph" aria-hidden="true"><Icon size={20} /></span>
                <h3>{t}</h3>
                <p>{d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="lp-section" id="security">
        <div className="lp-shell lp-security">
          <header className="lp-sec-head lp-sec-head-left">
            <span className="lp-eyebrow">Security</span>
            <h2 className="lp-h2">You're running code. We treat it that way.</h2>
            <p className="lp-sec-sub">Customer EAs are untrusted by default — isolation and review are the whole point.</p>
          </header>
          <ul className="lp-sec-list">
            <li><strong>One container per customer.</strong> Terminals are never shared, so no EA can see another customer's account or trades.</li>
            <li><strong>Source-only, scanned uploads.</strong> Compiled binaries are refused; every .mq5 is scanned for DLL imports before it compiles.</li>
            <li><strong>Hard resource caps.</strong> Per-customer RAM and CPU limits mean a leaking or runaway EA OOM-kills itself, not the platform.</li>
            <li><strong>Encrypted broker credentials.</strong> MT5 passwords are AES-256 encrypted at rest and never returned by the API.</li>
          </ul>
        </div>
      </section>

      {/* Pricing */}
      <section className="lp-section lp-section-alt" id="pricing">
        <div className="lp-shell">
          <header className="lp-sec-head">
            <span className="lp-eyebrow">Pricing</span>
            <h2 className="lp-h2">Start free. Scale when your EAs do.</h2>
            <p className="lp-sec-sub">Free while we're in beta — paid plans land with billing soon.</p>
          </header>
          <div className="lp-plans">
            {PLANS.map(p => (
              <article className={`lp-plan ${p.featured ? 'is-featured' : ''}`} key={p.name}>
                {p.featured && <span className="lp-plan-tag">Most popular</span>}
                <div className="lp-plan-name">{p.name}</div>
                <div className="lp-plan-price mono">{p.price} <span>{p.note}</span></div>
                <ul className="lp-plan-feats">
                  {p.feats.map(f => <li key={f}>{f}</li>)}
                </ul>
                <Link to="/register" className={`lp-btn ${p.featured ? 'lp-btn-primary' : 'lp-btn-outline'} lp-btn-block`}>{p.cta}</Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="lp-cta">
        <div className="lp-shell lp-cta-inner">
          <h2 className="lp-h2">Put your EAs on autopilot.</h2>
          <p>Create an account, upload an EA, and watch it go live — no VPS required.</p>
          <div className="lp-hero-actions">
            <Link to="/register" className="lp-btn lp-btn-primary lp-btn-lg">Create your account</Link>
            <Link to="/login" className="lp-btn lp-btn-ghost lp-btn-lg">Sign in</Link>
          </div>
        </div>
      </section>

      <footer className="lp-footer">
        <div className="lp-shell lp-footer-inner">
          <Link to="/" className="lp-brand"><span className="lp-mark" aria-hidden="true" />EAHost</Link>
          <p className="lp-foot-note">
            Running an Expert Advisor carries substantial risk. Hosting keeps your EA online; it
            does not guarantee profit. You are responsible for your own strategy.
          </p>
          <span className="lp-foot-copy mono">© {year.current}</span>
        </div>
      </footer>
    </div>
  );
}
